import React, { useEffect, useRef, useState } from 'react';
import { Typography } from '@mui/material';
import { fetchUserById, type UserDetails, updateUser, type UpdateUserPayload, type Role } from '../services/usersService';
import { fetchRoles, type RoleApiItem } from '../services/groupsService';
import DrawerForm from './DrawerForm';
import MultiSelect from './MultiSelect';
import AvatarImage from './AvatarImage';
import { useSecurity } from '../security/SecurityContext';

export interface UserDetailsDrawerProps {
  open: boolean;
  onClose: () => void;
  userId?: string | null; // if provided, fetch details
  onSaved?: () => void;
  isCreate?: boolean; // for create mode
  // When viewing an existing user, start directly in edit mode
  startInEdit?: boolean;
  // For create mode
  initialDraft?: {
    name: string;
    email: string;
    department: string;
    roles?: Role[];
    businessUnit: string;
    productLine: string;
  };
  businessUnitOptions?: string[];
  productLineOptions?: string[];
  onSaveCreate?: (data: {
    name: string;
    email: string;
    department: string;
    roles?: Role[];
    businessUnit: string;
    productLine: string;
    avatarUri?: string | null;
  }) => Promise<void>;
}

export default function UserDetailsDrawer(props: UserDetailsDrawerProps) {
  const {
    open,
    onClose,
    userId,
    onSaved,
    isCreate,
    startInEdit = false,
    initialDraft,
    businessUnitOptions = [],
    productLineOptions = [],
    onSaveCreate
  } = props;

  const [details, setDetails] = useState<UserDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  // For create mode
  const [createDraft, setCreateDraft] = useState(initialDraft || {
    name: "",
    email: "",
    department: "",
    roles: [],
    businessUnit: "",
    productLine: "",
  });

  const nameRef = useRef<HTMLInputElement | null>(null);
  const emailRef = useRef<HTMLInputElement | null>(null);
  const { user: currentUser, refresh: refreshSecurity } = useSecurity();

  // Group options: fetched from Roles API
  const [groupOptions, setGroupOptions] = useState<string[]>([]);
  const [roleLookupByName, setRoleLookupByName] = useState<Record<string, RoleApiItem>>({});
  const [selectedRoleNames, setSelectedRoleNames] = useState<string[]>([]);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!open) return;
      // Reset immediately to avoid showing stale data while loading
      setLoading(true);
      setError(null);
      setDetails(null);
      setSelectedRoleNames([]);
      if (!userId && !isCreate) { setLoading(false); return; }
      if (isCreate) { setIsEditing(true); setDetails(null); setLoading(false); return; }
      try {
        const data = await fetchUserById(userId!);
        if (!alive) return;
        setDetails(data || null);
        setIsEditing(Boolean(isCreate) || Boolean(startInEdit));
        // Initialize roles selection from API response
        // Use roles array only
        let initialRoles: string[] = [];
        if (Array.isArray(data.roles) && data.roles.length > 0) {
          // Extract names from Role objects
          initialRoles = data.roles.map((r: any) => r?.name || '').filter(Boolean);
        }
        setSelectedRoleNames(initialRoles);
      } catch (_e) {
        if (!alive) return;
        setDetails(null);
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [open, userId, isCreate, startInEdit]);

  // Load group options when drawer opens
  useEffect(() => {
    let cancelled = false;
    if (!open) return;
    (async () => {
      try {
        const resp = await fetchRoles({ name: '', description: '', permissions: '', textSearch: '', page: 1, pageSize: 100, skip: 1, includeDeleted: false, orderByField: 'Name', descending: true });
        const roles = Array.isArray(resp?.results) ? resp.results : [];
        const names = roles.map(r => (r?.name || '').trim()).filter(Boolean);
        const lookup = roles.reduce<Record<string, RoleApiItem>>((acc, r) => { if (r?.name) acc[r.name] = r; return acc; }, {});
        if (!cancelled) {
          setGroupOptions(names);
          setRoleLookupByName(lookup);
        }
      } catch (_e) {
        if (!cancelled) {
          setGroupOptions([]);
          setRoleLookupByName({});
        }
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  // Initialize create draft when opening in create mode
  useEffect(() => {
    if (open && isCreate) {
      setCreateDraft(initialDraft || {
        name: "",
        email: "",
        department: "",
        roles: [],
        businessUnit: businessUnitOptions[0] || "",
        productLine: productLineOptions[0] || "",
      });
    }
  }, [open, isCreate, initialDraft, businessUnitOptions, productLineOptions]);

  // Reset state when opening/closing
  useEffect(() => {
    if (!open) {
      setIsEditing(false);
      setSaving(false);
      setError(null);
      setSelectedRoleNames([]);
      setCreateDraft({
        name: "",
        email: "",
        department: "",
        roles: [],
        businessUnit: "",
        productLine: "",
      });
    }
  }, [open]);

  const handleSave = async () => {
    if (isCreate) {
      // Handle create mode
      if (!onSaveCreate) return;
      try {
        setSaving(true);
        setError(null);
        const rolesPayload = selectedRoleNames
          .map(n => roleLookupByName[n])
          .filter((r): r is RoleApiItem => Boolean(r))
          .map(r => ({ id: r.id, name: r.name, description: r.description, permissions: r.permissions }));
        await onSaveCreate({
          name: createDraft.name,
          email: createDraft.email,
          department: createDraft.department,
          roles: rolesPayload as any,
          businessUnit: createDraft.businessUnit,
          productLine: createDraft.productLine,
          avatarUri: null
        });
        onClose();
      } catch (e: any) {
        setError(e?.response?.data?.message || 'Failed to create user');
      } finally {
        setSaving(false);
      }
      return;
    }

    // Handle edit mode
    if (!details?.id) return;
    const isEditingCurrentUser = Boolean(currentUser && (
      (currentUser.id && currentUser.id === details.id) ||
      (currentUser.userId && currentUser.userId === details.userId) ||
      (currentUser.emailAddress && details.emailAddress && currentUser.emailAddress.toLowerCase() === details.emailAddress.toLowerCase())
    ));
    try {
      setSaving(true);
      setError(null);
      const rolesPayload = selectedRoleNames
        .map(n => roleLookupByName[n])
        .filter((r): r is RoleApiItem => Boolean(r))
        .map(r => ({ id: r.id, name: r.name, description: r.description, permissions: r.permissions }));
      const payload: UpdateUserPayload = {
        userId: details.userId,
        name: details.name,
        emailAddress: details.emailAddress,
        department: details.department || null,
        roles: rolesPayload as any,
        businessUnit: details.businessUnit || null,
        productLine: details.productLine || null,
        avatarUri: details.avatarUri ?? null,
        id: details.id,
        createdAt: details.createdAt ?? null,
        updatedAt: details.updatedAt ?? null,
      };
      await updateUser(details.id, payload);
      if (isEditingCurrentUser) {
        await refreshSecurity();
      }
      setIsEditing(false);
      if (typeof onSaved === 'function') onSaved();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const footerPrimarySecondary = (() => {
    if (!isEditing && !isCreate) {
      return {
        primary: { label: 'Edit', onClick: () => setIsEditing(true), disabled: false },
        secondary: undefined as any
      };
    }
    if (isEditing) {
      return {
        primary: { label: saving ? 'Saving…' : 'Save', onClick: handleSave, disabled: saving },
        secondary: { label: 'Cancel', onClick: () => { if (isCreate) { onClose(); } else { setIsEditing(false); } }, disabled: saving }
      };
    }
    return { primary: undefined as any, secondary: undefined as any };
  })();

  return (
    <DrawerForm
      open={open}
      onClose={onClose}
      title={isCreate ? 'New User' : 'User Details'}
      error={error}
      primaryAction={footerPrimarySecondary.primary}
      secondaryAction={footerPrimarySecondary.secondary}
    >
        {(!isCreate && loading) ? (
          <div className="p-4 text-sm text-gray-600">Loading…</div>
        ) : (
        <div className="space-y-6">
          {/* Profile Photo and upload */}
          <div>
            <div className="flex items-start gap-4">
              <div className="w-20 h-20 rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-center overflow-hidden">
                <AvatarImage
                  name={isCreate ? createDraft.name : (details?.name || '')}
                  url={details?.avatarUri || null}
                  filename={null}
                  size={80}
                />
              </div>
            </div>
          </div>

          {/* User Details block: view vs edit/create layouts */}
          {(isEditing || isCreate) ? (
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              {isCreate ? (
                // Create mode fields
                <>
                  <div>
                    <Typography variant="caption" className="text-gray-500 text-xs mb-0.5">User name</Typography>
                    <input
                      className="w-full bg-white border border-gray-300 rounded px-2 py-1 text-sm"
                      value={createDraft.name}
                      onChange={(e) => setCreateDraft(prev => ({ ...prev, name: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Typography variant="caption" className="text-gray-500 text-xs mb-0.5">Email</Typography>
                    <input
                      type="email"
                      className="w-full bg-white border border-gray-300 rounded px-2 py-1 text-sm"
                      value={createDraft.email}
                      onChange={(e) => setCreateDraft(prev => ({ ...prev, email: e.target.value }))}
                    />
                  </div>

                  <div>
                    <Typography variant="caption" className="text-gray-500 text-xs mb-0.5">Department</Typography>
                    <input
                      className="w-full bg-white border border-gray-300 rounded px-2 py-1 text-sm"
                      value={createDraft.department}
                      onChange={(e) => setCreateDraft(prev => ({ ...prev, department: e.target.value }))}
                    />
                  </div>

                  <div>
                    <MultiSelect
                      label="Roles"
                      options={(groupOptions || []).map(n => ({ id: n, label: n }))}
                      selectedIds={selectedRoleNames}
                      onSelectionChange={setSelectedRoleNames}
                      width="w-full"
                      dropdownWidth="w-full"
                      showAvatars={false}
                    />
                  </div>
                  <div>
                    <Typography variant="caption" className="text-gray-500 text-xs mb-0.5">Business Unit</Typography>
                    <select
                      className="w-full bg-white border border-gray-300 rounded px-2 py-1 text-sm"
                      value={createDraft.businessUnit}
                      onChange={(e) => setCreateDraft(prev => ({ ...prev, businessUnit: e.target.value }))}
                    >
                      <option value="">—</option>
                      {businessUnitOptions.map((o) => (
                        <option key={o} value={o}>{o}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Typography variant="caption" className="text-gray-500 text-xs mb-0.5">Product Line</Typography>
                    <select
                      className="w-full bg-white border border-gray-300 rounded px-2 py-1 text-sm"
                      value={createDraft.productLine}
                      onChange={(e) => setCreateDraft(prev => ({ ...prev, productLine: e.target.value }))}
                    >
                      <option value="">—</option>
                      {productLineOptions.map((o) => (
                        <option key={o} value={o}>{o}</option>
                      ))}
                    </select>
                  </div>

                </>
              ) : (
                // Edit mode fields (existing user)
                <>
                  <div>
                    <Typography variant="caption" className="text-gray-500 text-xs mb-0.5">User name</Typography>
                    <input
                      ref={nameRef}
                      className="w-full bg-white border border-gray-300 rounded px-2 py-1 text-sm"
                      disabled={loading}
                      value={details?.name || ''}
                      onChange={(e) => setDetails(d => d ? { ...d, name: e.target.value } : d)}
                    />
                  </div>
                  <div>
                    <Typography variant="caption" className="text-gray-500 text-xs mb-0.5">Email</Typography>
                    <input
                      ref={emailRef}
                      type="email"
                      className="w-full bg-white border border-gray-300 rounded px-2 py-1 text-sm"
                      disabled={loading}
                      value={details?.emailAddress || ''}
                      onChange={(e) => setDetails(d => d ? { ...d, emailAddress: e.target.value } : d)}
                    />
                  </div>

                  <div>
                    <Typography variant="caption" className="text-gray-500 text-xs mb-0.5">Department</Typography>
                    <input
                      className="w-full bg-white border border-gray-300 rounded px-2 py-1 text-sm"
                      value={details?.department || ''}
                      onChange={(e) => setDetails(d => d ? { ...d, department: e.target.value } : d)}
                    />
                  </div>

                  <div>
                    <MultiSelect
                      label="Roles"
                      options={(groupOptions || []).map(n => ({ id: n, label: n }))}
                      selectedIds={selectedRoleNames}
                      onSelectionChange={setSelectedRoleNames}
                      width="w-full"
                      dropdownWidth="w-full"
                      showAvatars={false}
                    />
                  </div>
                  <div>
                    <Typography variant="caption" className="text-gray-500 text-xs mb-0.5">Business Unit</Typography>
                    <select
                      className="w-full bg-white border border-gray-300 rounded px-2 py-1 text-sm"
                      value={details?.businessUnit || ''}
                      onChange={(e) => setDetails(d => d ? { ...d, businessUnit: e.target.value } : d)}
                    >
                      <option value="">—</option>
                      {businessUnitOptions.map((o) => (
                        <option key={o} value={o}>{o}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Typography variant="caption" className="text-gray-500 text-xs mb-0.5">Product Line</Typography>
                    <select
                      className="w-full bg-white border border-gray-300 rounded px-2 py-1 text-sm"
                      value={details?.productLine || ''}
                      onChange={(e) => setDetails(d => d ? { ...d, productLine: e.target.value } : d)}
                    >
                      <option value="">—</option>
                      {productLineOptions.map((o) => (
                        <option key={o} value={o}>{o}</option>
                      ))}
                    </select>
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="grid grid-cols-1 gap-6">
                <div>
                  <Typography variant="caption" className="text-gray-500 text-xs mb-0.5">User name</Typography>
                  <Typography variant="body2" className="font-semibold text-gray-900">{details?.name || '—'}</Typography>
                </div>
                <div>
                  <Typography variant="caption" className="text-gray-500 text-xs mb-0.5">Email</Typography>
                  <Typography variant="body2" className="font-semibold text-gray-900">{details?.emailAddress || '—'}</Typography>
                </div>

                <div>
                  <Typography variant="caption" className="text-gray-500 text-xs mb-0.5">Department</Typography>
                  <Typography variant="body2" className="font-semibold text-gray-900">{details?.department || '—'}</Typography>
                </div>

                <div>
                  <Typography variant="caption" className="text-gray-500 text-xs mb-0.5">Roles</Typography>
                  <div className="flex flex-wrap gap-1">
                    {details && Array.isArray(details.roles) && details.roles.length > 0 ? (
                      details.roles.map(role => (
                        <span key={role.id} className="text-[11px] px-2 py-0.5 rounded bg-[#E6F0FA] text-[#1D5176] border border-[#C9DDF1]">{role.name}</span>
                      ))
                    ) : (
                      <Typography variant="body2" className="text-gray-500 text-sm">—</Typography>
                    )}
                  </div>
                </div>
                <div>
                  <Typography variant="caption" className="text-gray-500 text-xs mb-0.5">Business Unit</Typography>
                  <Typography variant="body2" className="font-semibold text-gray-900">{details?.businessUnit || '—'}</Typography>
                </div>
                <div>
                  <Typography variant="caption" className="text-gray-500 text-xs mb-0.5">Product Line</Typography>
                  <Typography variant="body2" className="font-semibold text-gray-900">{details?.productLine || '—'}</Typography>
                </div>
              </div>
            </div>
          )}
        </div>
        )}
    </DrawerForm>
  );
}


